import type { Stroke } from '../../types';
import getStroke from 'perfect-freehand';

export type StrokeOutlinePoint = [number, number];
export type StrokePathOffset = { x: number; y: number };

export type StrokePathOps = {
  moveTo: (x: number, y: number) => void;
  quadTo: (cx: number, cy: number, x: number, y: number) => void;
  lineTo: (x: number, y: number) => void;
  close: () => void;
};

const linearEasing = (t: number): number => t;

export const getStrokeOutlinePoints = (stroke: Stroke): StrokeOutlinePoint[] => {
  // Convert points to format expected by perfect-freehand: [x, y, pressure]
  const inputPoints = stroke.points.map(p => [p.x, p.y, p.pressure] as [number, number, number]);

  // Detect if we have real pressure data (pen tablet) vs simulated (mouse)
  // If any point has pressure != 0.5 (our default), we have real pressure data
  const hasRealPressure = stroke.points.some(p => p.pressure !== 0.5);

  return getStroke(inputPoints, {
    size: stroke.baseWidth,
    thinning: 0.7,
    smoothing: 0.5,
    streamline: 0.5,
    easing: linearEasing,
    simulatePressure: !hasRealPressure,
    start: {
      taper: 0,
      cap: true,
    },
    end: {
      taper: 0,
      cap: true,
    },
  }) as StrokeOutlinePoint[];
};

export const walkStrokeOutline = (
  outlinePoints: StrokeOutlinePoint[],
  ops: StrokePathOps,
  offset: StrokePathOffset = { x: 0, y: 0 }
): boolean => {
  if (outlinePoints.length === 0) return false;

  const [startX, startY] = outlinePoints[0]!;
  ops.moveTo(startX + offset.x, startY + offset.y);

  if (outlinePoints.length > 2) {
    for (let i = 1; i < outlinePoints.length - 1; i++) {
      const [p0x, p0y] = outlinePoints[i]!;
      const [p1x, p1y] = outlinePoints[i + 1]!;
      const midX = (p0x + p1x) / 2 + offset.x;
      const midY = (p0y + p1y) / 2 + offset.y;
      ops.quadTo(p0x + offset.x, p0y + offset.y, midX, midY);
    }

    const [lastX, lastY] = outlinePoints[outlinePoints.length - 1]!;
    ops.lineTo(lastX + offset.x, lastY + offset.y);
  } else {
    for (let i = 1; i < outlinePoints.length; i++) {
      const [x, y] = outlinePoints[i]!;
      ops.lineTo(x + offset.x, y + offset.y);
    }
  }

  ops.close();
  return true;
};

export const drawStrokeOutline = (
  ctx: CanvasRenderingContext2D,
  outlinePoints: StrokeOutlinePoint[],
  offset?: StrokePathOffset
): boolean =>
  walkStrokeOutline(
    outlinePoints,
    {
      moveTo: (x, y): void => ctx.moveTo(x, y),
      quadTo: (cx, cy, x, y): void => ctx.quadraticCurveTo(cx, cy, x, y),
      lineTo: (x, y): void => ctx.lineTo(x, y),
      close: (): void => ctx.closePath(),
    },
    offset
  );

export const renderStroke = (
  stroke: Stroke,
  ctx: CanvasRenderingContext2D,
): void => {
  const { points, color, tool } = stroke;
  const isEraser = tool === 'eraser';
  ctx.globalCompositeOperation = isEraser ? 'destination-out' : 'source-over';

  if (points.length === 0) {
    ctx.globalCompositeOperation = 'source-over';
    return;
  }

  const outlinePoints = getStrokeOutlinePoints(stroke);
  ctx.fillStyle = isEraser ? '#000000' : color;
  ctx.beginPath();
  if (!drawStrokeOutline(ctx, outlinePoints)) {
    ctx.globalCompositeOperation = 'source-over';
    return;
  }
  ctx.fill();
  ctx.globalCompositeOperation = 'source-over';
};

export const buildStrokeSvgPath = (
  outlinePoints: StrokeOutlinePoint[],
  offset: StrokePathOffset = { x: 0, y: 0 }
): string => {
  let d = '';
  const hasPath = walkStrokeOutline(
    outlinePoints,
    {
      moveTo: (x, y): void => {
        d += `M ${x} ${y}`;
      },
      quadTo: (cx, cy, x, y): void => {
        d += ` Q ${cx} ${cy} ${x} ${y}`;
      },
      lineTo: (x, y): void => {
        d += ` L ${x} ${y}`;
      },
      close: (): void => {
        d += ' Z';
      },
    },
    offset
  );
  return hasPath ? d : '';
};
